import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Skeleton, Tag } from 'antd';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import { Sidebar } from '@/components/sidebar';
import { API } from '@/services/api.service';
import { Facts, ListToolbar, Sums } from './Filters';
import { columnKeysOf, date, listQueryOf, money, noop } from './helpers';
import useColumnHeaders from './useColumnHeaders';

const FILTERS = ['calculationNumber', 'kind', 'vatPeriod', 'documentType', 'relatedParty', 'internalInvoiceNumber', 'status', 'recordingDate'];
const SORTS = ['calculationNumber', 'kind', 'vatPeriod', 'documentType', 'relatedParty', 'internalInvoiceNumber', 'totalVat', 'status', 'recordingDate'];
const KEYS = columnKeysOf(FILTERS, SORTS);

/** VAT calculation records (PEP) the company filed on SEF. A row opens the record. */
export default function VatRecordsTab({ company, queryParams, setQueryParams }) {
  const { t } = useTranslation('sefState');
  const [selected, setSelected] = useState(null);
  const query = listQueryOf(queryParams);
  const list = useQuery({
    queryKey: ['sefState', company, 'vatRecords', query],
    queryFn: () => API.sefState.getVatRecords(company, query),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const { data } = list;
  const h = useColumnHeaders(queryParams, setQueryParams, FILTERS);
  const c = (key) => t(`vatRecords.columns.${key}`);
  const opts = (key) => (data?.options?.[key] || []).map((v) => ({ value: v, label: v }));
  const kinds = ['individual', 'group'].map((k) => ({ value: k, label: t(`vatRecords.kinds.${k}`) }));

  const columns = [
    {
      title: h.text('calculationNumber', c('calculationNumber')),
      dataIndex: 'calculationNumber',
      width: 220,
      render: (v) => <TruncateTooltipCell label={v} className="font-semibold" />,
    },
    {
      title: h.options('kind', c('kind'), kinds),
      dataIndex: 'kind',
      width: 140,
      render: (k) => <Tag>{t(`vatRecords.kinds.${k}`)}</Tag>,
    },
    { title: h.options('vatPeriod', c('vatPeriod'), opts('vatPeriod')), dataIndex: 'vatPeriod', width: 130, render: (v, r) => `${v}/${r.year}` },
    { title: h.options('documentType', c('documentType'), opts('documentType')), dataIndex: 'documentType', width: 160 },
    { title: h.text('relatedParty', c('relatedParty')), dataIndex: 'relatedParty', width: 170, render: (v) => <TruncateTooltipCell label={v} /> },
    {
      title: h.text('internalInvoiceNumber', c('internalInvoiceNumber')),
      dataIndex: 'internalInvoiceNumber',
      width: 200,
      render: (v) => <TruncateTooltipCell label={v} />,
    },
    { title: h.sort('totalVat', c('totalVat')), dataIndex: 'totalVat', width: 150, align: 'right', render: money },
    { title: h.options('status', c('status'), opts('status')), dataIndex: 'status', width: 120 },
    { title: h.date('recordingDate', c('recordingDate')), dataIndex: 'recordingDate', width: 140, render: date },
  ];
  const labels = Object.fromEntries(
    ['calculationNumber', 'kind', 'year', 'vatPeriod', 'documentType', 'relatedParty', 'totalVat', 'status', 'recordingDate'].map((k) => [k, c(k)])
  );

  return (
    <>
      <DataTable
        rowKey="_id"
        title={`${t('vatRecords.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={list.isLoading ? 'pending' : list.error ? 'error' : 'success'}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        onRow={(r) => ({ onClick: () => setSelected(r._id), className: 'cursor-pointer' })}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, col) => s + (col.width || 0), 0) }}
        size="small"
        headerChildren={
          <ListToolbar
            keys={KEYS}
            setQueryParams={setQueryParams}
            exportProps={{ company, kind: 'vatRecording', filters: query, labels, disabled: !data?.count }}
          />
        }
      />
      {data && <Sums items={[[t('vatRecords.sums.totalVat'), money(data.sums.totalVat)]]} />}
      <VatRecordDetail company={company} id={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function VatRecordDetail({ company, id, onClose }) {
  const { t } = useTranslation('sefState');
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['sefState', company, 'vatRecords', 'one', id],
    queryFn: () => API.sefState.getVatRecordOne(company, id),
    enabled: Boolean(id),
  });
  const c = (key) => t(`vatRecords.columns.${key}`);
  const raw = d?.raw || {};

  return (
    <Sidebar title={d?.calculationNumber || t('vatRecords.title')} open={Boolean(id)} onClose={onClose} className="max-w-xl">
      <div className="grid gap-4 p-4">
        {isLoading && <Skeleton active paragraph={{ rows: 10 }} />}
        {error && <Alert type="error" showIcon message={t('errors.load')} />}
        {d && (
          <>
            <Facts
              items={[
                [c('calculationNumber'), d.calculationNumber],
                [c('kind'), t(`vatRecords.kinds.${d.kind}`)],
                [c('vatPeriod'), `${d.vatPeriod}/${d.year}`],
                [c('documentType'), d.documentType],
                [c('relatedParty'), d.relatedParty],
                [c('internalInvoiceNumber'), d.internalInvoiceNumber],
                d.documentNumber && [t('vatRecords.detail.documentNumber'), d.documentNumber],
                [c('totalVat'), money(d.totalVat)],
                [c('status'), d.status],
                [c('recordingDate'), date(d.recordingDate)],
                [t('vatRecords.detail.statusChangeDate'), date(d.statusChangeDate)],
                raw.PrepaymentDate && [t('vatRecords.detail.prepaymentDate'), date(raw.PrepaymentDate)],
                raw.BasisForPrepayment && [t('vatRecords.detail.basisForPrepayment'), raw.BasisForPrepayment],
              ]}
            />
            <p className="text-xs opacity-60">SEF Id {d.sefId}</p>
          </>
        )}
      </div>
    </Sidebar>
  );
}
